import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { getFaqs, addFaq, deleteFaq, editFaq, uploadBulkFaqs } from '../../controllers/chatbot.controller';
import toast from 'react-hot-toast';
import { 
  IconTrash, 
  IconPlus, 
  IconPhoto, 
  IconEdit, 
  IconUpload, 
  IconDownload, 
  IconFileSpreadsheet, 
  IconFileText, 
  IconCheck, 
  IconX, 
  IconAlertCircle, 
  IconSearch, 
  IconSparkles,
  IconHelpCircle
} from '@tabler/icons-react';
import { API } from '../../config/config';

export default function ChatbotTrainingPage() {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  
  // Bulk upload states
  const [bulkFileName, setBulkFileName] = useState('');
  const [parsedFaqs, setParsedFaqs] = useState([]);

  const [formData, setFormData] = useState({
    question: '',
    answer_text: '',
    keywords: '',
    image: null,
    is_featured: false
  });

  const BASE_URL = API.replace('/api/v1', '');

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    setIsLoading(true);
    try {
      const data = await getFaqs();
      setFaqs(Array.isArray(data) ? data : (data.faqs || []));
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFaqs = useMemo(() => {
    if (!searchFilter.trim()) return faqs;
    const q = searchFilter.toLowerCase().trim();
    return faqs.filter(faq => 
      (faq.question && faq.question.toLowerCase().includes(q)) ||
      (faq.keywords && faq.keywords.toLowerCase().includes(q)) ||
      (faq.answer_text && faq.answer_text.toLowerCase().includes(q))
    );
  }, [faqs, searchFilter]);

  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const form = new FormData();
      form.append('question', formData.question);
      form.append('answer_text', formData.answer_text);
      if (formData.keywords) form.append('keywords', formData.keywords);
      if (formData.image) form.append('image', formData.image);
      form.append('is_featured', formData.is_featured);
      
      if (editingId) {
        await editFaq(editingId, form);
        toast.success('FAQ Updated Successfully');
      } else {
        await addFaq(form);
        toast.success('FAQ Added Successfully');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ question: '', answer_text: '', keywords: '', image: null, is_featured: false });
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (faq) => {
    setEditingId(faq.id);
    setFormData({
      question: faq.question,
      answer_text: faq.answer_text,
      keywords: faq.keywords || '',
      image: null,
      is_featured: faq.is_featured === 1
    });
    setShowForm(true);
    setShowBulkModal(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ question: '', answer_text: '', keywords: '', image: null, is_featured: false });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    
    try {
      await deleteFaq(id);
      toast.success('FAQ Deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  // =================== BULK IMPORT & TEMPLATES ===================
  const sampleTemplateRows = [
    {
      "question": "What are your restaurant opening hours?",
      "keyword": "hours, timing, open, close, schedule, time",
      "answer text": "We are open from 9:00 AM to 10:00 PM every day from Monday to Sunday.",
      "feature this question": "Yes"
    },
    {
      "question": "Do you offer table reservations?",
      "keyword": "reservation, book table, booking, reserve, seat",
      "answer text": "Yes, you can book a table directly through our website or by contacting front desk.",
      "feature this question": "Yes"
    },
    {
      "question": "Where are you located?",
      "keyword": "location, address, map, directions, where",
      "answer text": "We are located at Lakeside Pokhara, Nepal. Parking is available on-site.",
      "feature this question": "No"
    },
    {
      "question": "Do you accept credit cards and QR payments?",
      "keyword": "payment, card, visa, mastercard, cash, fonepay, esewa, qr",
      "answer text": "Yes, we accept Cash, Credit/Debit Cards, and QR payments (FonePay / eSewa).",
      "feature this question": "No"
    }
  ];

  const handleDownloadExcelTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet(sampleTemplateRows);
    worksheet['!cols'] = [
      { wch: 42 }, // question
      { wch: 38 }, // keyword
      { wch: 60 }, // answer text
      { wch: 24 }  // feature this question
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chatbot_FAQs");
    XLSX.writeFile(workbook, "chatbot_qa_bulk_template.xlsx");
    toast.success("Excel template downloaded!");
  };

  const handleDownloadCsvTemplate = () => {
    const csv = Papa.unparse(sampleTemplateRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "chatbot_qa_bulk_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV template downloaded!");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    const extension = file.name.split('.').pop().toLowerCase();

    if (extension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          processRawImportData(results.data, file.name);
        },
        error: (err) => {
          toast.error("Error reading CSV file");
          console.error(err);
        }
      });
    } else if (extension === 'xlsx' || extension === 'xls') {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const bstr = evt.target.result;
          const workbook = XLSX.read(bstr, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          processRawImportData(rawData, file.name);
        } catch (err) {
          toast.error("Error reading Excel file");
          console.error(err);
        }
      };
      reader.readAsBinaryString(file);
    } else {
      toast.error("Please upload an Excel (.xlsx / .xls) or CSV (.csv) file");
    }
  };

  const processRawImportData = (rows, filename) => {
    if (!rows || rows.length === 0) {
      toast.error("Uploaded file has no data rows");
      setParsedFaqs([]);
      return;
    }

    const processed = rows.map((row, index) => {
      // Find question
      const question = (
        row["question"] || row["Question"] || row["Question Text"] || row["question_text"] || ""
      ).toString().trim();

      // Find keyword
      const keyword = (
        row["keyword"] || row["Keyword"] || row["keywords"] || row["Keywords"] || ""
      ).toString().trim();

      // Find answer text
      const answer = (
        row["answer text"] || row["Answer text"] || row["Answer Text"] || row["answer_text"] || row["answer"] || row["Answer"] || ""
      ).toString().trim();

      // Find feature this question
      const featuredRaw = (
        row["feature this question"] ?? row["Feature this question"] ?? row["Feature This Question"] ??
        row["is_featured"] ?? row["Is_Featured"] ?? row["featured"] ?? row["Featured"] ?? ""
      ).toString().trim().toLowerCase();

      const isFeatured = featuredRaw === "yes" || featuredRaw === "true" || featuredRaw === "1" || featuredRaw === "y";

      return {
        id: index + 1,
        question,
        keyword,
        answer_text: answer,
        is_featured: isFeatured,
        isValid: Boolean(question && answer)
      };
    });

    setParsedFaqs(processed);
    const validCount = processed.filter(p => p.isValid).length;
    if (validCount > 0) {
      toast.success(`Loaded ${validCount} questions from ${filename}`);
    } else {
      toast.error("No valid questions found. Ensure 'question' and 'answer text' columns exist.");
    }
  };

  const handleBulkImport = async () => {
    const validRows = parsedFaqs.filter(f => f.isValid);
    if (validRows.length === 0) {
      toast.error("No valid questions to import");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = validRows.map(r => ({
        question: r.question,
        keywords: r.keyword,
        answer_text: r.answer_text,
        is_featured: r.is_featured
      }));

      const res = await uploadBulkFaqs(payload);
      toast.success(res.message || `Successfully imported ${validRows.length} FAQs!`);
      setShowBulkModal(false);
      setParsedFaqs([]);
      setBulkFileName("");
      fetchFaqs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to import FAQs");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validParsedCount = parsedFaqs.filter(f => f.isValid).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <IconSparkles className="text-primary" size={32} /> Chatbot Knowledge Base
          </h1>
          <p className="text-gray-500 mt-1">Train the AI & rule-based chatbot by providing Q&A pairs, trigger keywords, and featured quick questions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => {
              setShowBulkModal(!showBulkModal);
              if (showForm) setShowForm(false);
            }}
            className={`btn ${showBulkModal ? 'btn-neutral' : 'btn-outline border-primary text-primary hover:bg-primary hover:text-white'}`}
          >
            <IconUpload size={20} /> Bulk Import Q&A
          </button>
          <button 
            onClick={() => {
              setEditingId(null);
              setFormData({ question: '', answer_text: '', keywords: '', image: null, is_featured: false });
              setShowForm(!showForm);
              if (showBulkModal) setShowBulkModal(false);
            }}
            className="btn btn-primary"
          >
            <IconPlus size={20} /> Add New FAQ
          </button>
        </div>
      </div>

      {/* BULK UPLOAD SECTION */}
      {showBulkModal && (
        <div className="card bg-white shadow-xl border border-primary/20 mb-8 overflow-hidden animate-fade-in-down">
          <div className="bg-primary/5 px-6 py-4 border-b border-primary/10 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <IconUpload className="text-primary" size={22} /> Bulk Upload Rule-Based Questions & Answers
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Upload multiple questions, keywords, and answers at once using an Excel or CSV template.
              </p>
            </div>
            <button 
              onClick={() => {
                setShowBulkModal(false);
                setParsedFaqs([]);
                setBulkFileName("");
              }}
              className="btn btn-sm btn-circle btn-ghost text-gray-400 hover:text-gray-700"
            >
              <IconX size={20} />
            </button>
          </div>

          <div className="card-body p-6 space-y-6">
            {/* Step 1: Download Templates */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200/80">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">1</span>
                    Download Sample Format Template
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Download the pre-formatted Excel or CSV file with sample questions, keywords, answers, and featured flags.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button 
                    onClick={handleDownloadExcelTemplate}
                    className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none gap-2 shadow-sm"
                  >
                    <IconFileSpreadsheet size={18} /> Excel Format (.xlsx)
                  </button>
                  <button 
                    onClick={handleDownloadCsvTemplate}
                    className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white border-none gap-2 shadow-sm"
                  >
                    <IconFileText size={18} /> CSV Format (.csv)
                  </button>
                </div>
              </div>

              {/* Column specifications guide */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-3 border-t border-gray-200 text-xs">
                <div className="bg-white p-2.5 rounded-lg border border-gray-200">
                  <span className="font-bold text-gray-900 block">question *</span>
                  <span className="text-gray-500">The customer question or query to be answered.</span>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-gray-200">
                  <span className="font-bold text-gray-900 block">keyword</span>
                  <span className="text-gray-500">Comma-separated keywords that trigger this response.</span>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-gray-200">
                  <span className="font-bold text-gray-900 block">answer text *</span>
                  <span className="text-gray-500">The automatic reply text sent by the chatbot.</span>
                </div>
                <div className="bg-white p-2.5 rounded-lg border border-gray-200">
                  <span className="font-bold text-gray-900 block">feature this question</span>
                  <span className="text-gray-500">Enter <span className="font-semibold text-emerald-600">Yes</span> or <span className="font-semibold text-gray-500">No</span> to show as a quick question button.</span>
                </div>
              </div>
            </div>

            {/* Step 2: File Upload Area */}
            <div>
              <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">2</span>
                Select & Upload Your File
              </h3>
              
              <div className="border-2 border-dashed border-gray-300 hover:border-primary/60 rounded-2xl p-6 text-center transition-all bg-gray-50/50 hover:bg-primary/5">
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-3">
                  <IconUpload size={24} />
                </div>
                <label className="cursor-pointer">
                  <span className="text-sm font-semibold text-primary hover:underline">Click to browse</span>
                  <span className="text-sm text-gray-500"> or drag and drop your Excel (.xlsx, .xls) or CSV (.csv) file</span>
                  <input 
                    type="file" 
                    accept=".xlsx, .xls, .csv" 
                    onChange={handleFileChange} 
                    className="hidden" 
                  />
                </label>
                {bulkFileName && (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-700 shadow-sm">
                    <IconFileSpreadsheet size={16} className="text-emerald-600" />
                    <span>{bulkFileName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Parsed Preview */}
            {parsedFaqs.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">3</span>
                    Preview Parsed Questions ({validParsedCount} valid of {parsedFaqs.length})
                  </h3>
                  <button 
                    onClick={() => { setParsedFaqs([]); setBulkFileName(""); }}
                    className="btn btn-xs btn-ghost text-error"
                  >
                    Clear Preview
                  </button>
                </div>

                <div className="overflow-x-auto max-h-72 border border-gray-200 rounded-xl bg-white shadow-sm">
                  <table className="table table-xs table-pin-rows">
                    <thead>
                      <tr className="bg-gray-100 text-gray-700">
                        <th>#</th>
                        <th>Question</th>
                        <th>Keywords</th>
                        <th>Answer Text</th>
                        <th>Feature This Question</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedFaqs.map((row) => (
                        <tr key={row.id} className={!row.isValid ? "bg-red-50/50" : ""}>
                          <td>{row.id}</td>
                          <td className="font-medium max-w-xs truncate" title={row.question}>
                            {row.question || <span className="text-error italic">Missing Question</span>}
                          </td>
                          <td className="max-w-xs truncate text-gray-500" title={row.keyword}>
                            {row.keyword || "-"}
                          </td>
                          <td className="max-w-md truncate text-gray-600" title={row.answer_text}>
                            {row.answer_text || <span className="text-error italic">Missing Answer</span>}
                          </td>
                          <td>
                            {row.is_featured ? (
                              <span className="badge badge-xs badge-primary font-medium">Yes (Featured)</span>
                            ) : (
                              <span className="badge badge-xs badge-ghost">No</span>
                            )}
                          </td>
                          <td>
                            {row.isValid ? (
                              <span className="text-emerald-600 font-semibold flex items-center gap-1">
                                <IconCheck size={14} /> Ready
                              </span>
                            ) : (
                              <span className="text-error font-semibold flex items-center gap-1">
                                <IconAlertCircle size={14} /> Incomplete
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end items-center gap-3 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => {
                      setShowBulkModal(false);
                      setParsedFaqs([]);
                      setBulkFileName("");
                    }} 
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleBulkImport} 
                    disabled={isSubmitting || validParsedCount === 0}
                    className="btn btn-primary px-8"
                  >
                    {isSubmitting ? (
                      <span className="loading loading-spinner"></span>
                    ) : (
                      <>
                        <IconUpload size={18} /> Confirm & Import {validParsedCount} FAQs
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SINGLE ADD / EDIT FAQ FORM */}
      {showForm && (
        <div className="card bg-white shadow-lg border border-gray-100 mb-8 animate-fade-in-down">
          <div className="card-body">
            <h2 className="card-title text-xl mb-4">{editingId ? 'Edit Training Data' : 'Add Training Data'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label"><span className="label-text font-medium">Question (Example format)</span></label>
                <input required type="text" name="question" value={formData.question} onChange={handleInputChange} className="input input-bordered w-full" placeholder="e.g. Can I preview the POS?" />
              </div>
              
              <div className="form-control">
                <label className="label"><span className="label-text font-medium">Keywords (Comma separated)</span></label>
                <input type="text" name="keywords" value={formData.keywords} onChange={handleInputChange} className="input input-bordered w-full" placeholder="e.g. preview, POS, screenshot, see" />
                <span className="text-xs text-gray-400 mt-1">These keywords help the bot match user queries even if they don't type the exact question.</span>
              </div>
              
              <div className="form-control">
                <label className="label"><span className="label-text font-medium">Answer Text</span></label>
                <textarea required name="answer_text" value={formData.answer_text} onChange={handleInputChange} className="textarea textarea-bordered h-24" placeholder="Type the answer the bot should give..."></textarea>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange} className="checkbox checkbox-primary" />
                  <span className="label-text font-medium">Feature this as a Quick Question in the Chatbot</span>
                </label>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium flex items-center gap-2"><IconPhoto size={18}/> Attach Image (Optional)</span>
                </label>
                <input type="file" name="image" onChange={handleInputChange} accept="image/*" className="file-input file-input-bordered w-full max-w-xs" />
                {editingId && <span className="text-xs text-gray-400 mt-1">Leave empty to keep the existing image.</span>}
              </div>
              
              <div className="form-control mt-6 flex-row gap-4">
                <button type="submit" className="btn btn-primary px-8" disabled={isSubmitting}>
                  {isSubmitting ? <span className="loading loading-spinner"></span> : (editingId ? 'Update FAQ' : 'Save Training Data')}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SEARCH AND FILTER BAR */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <div className="relative w-full sm:w-96">
          <IconSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text"
            placeholder="Search questions, keywords, or answers..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="input input-sm input-bordered pl-10 w-full rounded-xl"
          />
        </div>
        <div className="text-xs text-gray-500">
          Showing <span className="font-bold text-gray-800">{filteredFaqs.length}</span> of {faqs.length} FAQs
        </div>
      </div>

      {/* FAQS LIST */}
      {isLoading ? (
        <div className="flex justify-center p-12"><span className="loading loading-spinner loading-lg text-primary"></span></div>
      ) : faqs.length === 0 ? (
        <div className="text-center p-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <IconHelpCircle size={48} className="mx-auto text-gray-400 mb-2" />
          <h3 className="font-bold text-gray-700">No training data found</h3>
          <p className="text-gray-500 text-sm mt-1">Add individual FAQs or use Bulk Import to populate your chatbot knowledge base.</p>
          <div className="mt-4 flex justify-center gap-3">
            <button onClick={() => setShowBulkModal(true)} className="btn btn-sm btn-outline border-primary text-primary">
              <IconUpload size={16} /> Bulk Import Q&A
            </button>
            <button onClick={() => setShowForm(true)} className="btn btn-sm btn-primary">
              <IconPlus size={16} /> Add First FAQ
            </button>
          </div>
        </div>
      ) : filteredFaqs.length === 0 ? (
        <div className="text-center p-12 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-gray-500">No questions match "{searchFilter}".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredFaqs.map(faq => (
            <div key={faq.id} className="card bg-base-100 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="card-body p-6">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg text-gray-900">{faq.question}</h3>
                    {faq.is_featured === 1 && <div className="badge badge-primary badge-sm font-semibold">Featured</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEditClick(faq)} className="btn btn-ghost btn-sm btn-circle text-blue-500" title="Edit">
                      <IconEdit size={18} />
                    </button>
                    <button onClick={() => handleDelete(faq.id)} className="btn btn-ghost btn-sm btn-circle text-error" title="Delete">
                      <IconTrash size={18} />
                    </button>
                  </div>
                </div>
                
                {faq.keywords && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {faq.keywords.split(',').map((kw, i) => (
                      <span key={i} className="badge badge-outline text-xs bg-gray-50 text-gray-600">{kw.trim()}</span>
                    ))}
                  </div>
                )}
                
                <div className="divider my-2"></div>
                
                <p className="text-gray-600 whitespace-pre-wrap text-sm leading-relaxed">{faq.answer_text}</p>
                
                {faq.answer_image && (
                  <div className="mt-4 rounded-lg overflow-hidden border border-gray-200">
                    <img src={faq.answer_image.startsWith('http') ? faq.answer_image : `${BASE_URL}/${faq.answer_image}`} alt="Preview" className="w-full h-auto object-cover max-h-48" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
